import { dealFromPermutation } from './deal.js';
/** Bumped from 1 with the seat commitments: a v1 receipt and a v2 receipt are
 *  different claims, and every domain tag below carries the version so a v1
 *  digest can never be mistaken for a v2 one. */
export const FAIRNESS_VERSION = 2;
/** Seeds are fixed-width, which is what lets the deal-seed preimage skip separators. */
export const SEED_BYTES = 32;
export const COMMIT_DOMAIN = 'cards.deal.commit.v2';
/** Distinct tag for the per-seat fill commitments: the same seed hashed under
 *  two tags must not produce the same digest, or one commitment could be
 *  presented as the other. */
export const SEAT_COMMIT_DOMAIN = 'cards.deal.fill.v2';
export const DEAL_SEED_DOMAIN = 'cards.deal.seed.v2';
/** `FairnessPublic.contributions` is typed as strings, so these two markers live
 *  in the same slot as a seat's revealed seed: 'pending' before it arrives,
 *  'server' when the server filled in for a bot or a slow client. */
export const CONTRIBUTION_PENDING = 'pending';
export const CONTRIBUTION_SERVER = 'server';
// ---------------------------------------------------------------- SHA-256
const K = new Uint32Array([
    0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
    0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
    0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
    0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
    0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
    0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
    0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
    0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);
const rotr = (x, n) => (x >>> n) | (x << (32 - n));
export function sha256(message) {
    const h = new Uint32Array([
        0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
    ]);
    const len = message.length;
    const padded = new Uint8Array(((((len + 8) >> 6) + 1) << 6));
    padded.set(message);
    padded[len] = 0x80;
    const view = new DataView(padded.buffer);
    const bits = len * 8;
    view.setUint32(padded.length - 8, Math.floor(bits / 0x100000000));
    view.setUint32(padded.length - 4, bits >>> 0);
    const w = new Uint32Array(64);
    for (let off = 0; off < padded.length; off += 64) {
        for (let i = 0; i < 16; i++)
            w[i] = view.getUint32(off + i * 4);
        for (let i = 16; i < 64; i++) {
            const a = w[i - 15];
            const b = w[i - 2];
            const s0 = rotr(a, 7) ^ rotr(a, 18) ^ (a >>> 3);
            const s1 = rotr(b, 17) ^ rotr(b, 19) ^ (b >>> 10);
            w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
        }
        let a = h[0], b = h[1], c = h[2], d = h[3], e = h[4], f = h[5], g = h[6], hh = h[7];
        for (let i = 0; i < 64; i++) {
            const s1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
            const ch = (e & f) ^ (~e & g);
            const t1 = (hh + s1 + ch + K[i] + w[i]) >>> 0;
            const s0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
            const maj = (a & b) ^ (a & c) ^ (b & c);
            const t2 = (s0 + maj) >>> 0;
            hh = g;
            g = f;
            f = e;
            e = (d + t1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (t1 + t2) >>> 0;
        }
        h[0] = (h[0] + a) >>> 0;
        h[1] = (h[1] + b) >>> 0;
        h[2] = (h[2] + c) >>> 0;
        h[3] = (h[3] + d) >>> 0;
        h[4] = (h[4] + e) >>> 0;
        h[5] = (h[5] + f) >>> 0;
        h[6] = (h[6] + g) >>> 0;
        h[7] = (h[7] + hh) >>> 0;
    }
    const out = new Uint8Array(32);
    const outView = new DataView(out.buffer);
    for (let i = 0; i < 8; i++)
        outView.setUint32(i * 4, h[i]);
    return out;
}
// ---------------------------------------------------------------- bytes & hex
const HEX = '0123456789abcdef';
export function bytesToHex(bytes) {
    let s = '';
    for (let i = 0; i < bytes.length; i++)
        s += HEX[bytes[i] >> 4] + HEX[bytes[i] & 15];
    return s;
}
export function hexToBytes(hex) {
    if (hex.length % 2 !== 0)
        throw new Error(`odd-length hex: ${hex.length}`);
    const out = new Uint8Array(hex.length / 2);
    for (let i = 0; i < out.length; i++) {
        const byte = Number.parseInt(hex.slice(i * 2, i * 2 + 2), 16);
        if (Number.isNaN(byte))
            throw new Error('non-hex character in seed');
        out[i] = byte;
    }
    return out;
}
/** Lowercase only: the verifier compares hex strings with `===`, and mixed case
 *  would make two spellings of the same seed look like two different seeds. */
export function isSeedHex(value) {
    return typeof value === 'string' && value.length === SEED_BYTES * 2 && /^[0-9a-f]+$/.test(value);
}
export function sha256Hex(message) {
    return bytesToHex(sha256(message));
}
const utf8 = new TextEncoder();
function concat(parts) {
    const total = parts.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(total);
    let at = 0;
    for (const p of parts) {
        out.set(p, at);
        at += p.length;
    }
    return out;
}
function uint32be(n) {
    const out = new Uint8Array(4);
    new DataView(out.buffer).setUint32(0, n >>> 0);
    return out;
}
const ZERO = new Uint8Array([0]);
/** 32 bytes from the platform CSPRNG. Present as `globalThis.crypto` in Node 20+
 *  and every target browser, so no conditional import is needed. */
export function randomSeedHex() {
    // Structurally typed rather than via lib.dom, so the package needs neither the
    // DOM lib nor @types/node to compile for both targets.
    const webcrypto = globalThis.crypto;
    if (!webcrypto?.getRandomValues) {
        throw new Error('no CSPRNG available: fairness seeds require WebCrypto or node:crypto');
    }
    return bytesToHex(webcrypto.getRandomValues(new Uint8Array(SEED_BYTES)));
}
// ---------------------------------------------------------------- the scheme
/**
 * The server's own commitment, published before anyone sees a card. Bound to
 * (match, hand) so a commitment cannot be replayed for a different deal, and
 * 0x00-separated so that no two distinct (matchId, handIndex) pairs can produce
 * the same preimage.
 */
export function commitment(matchId, handIndex, serverSeed) {
    assertSeed(serverSeed, 'serverSeed');
    return sha256Hex(concat([
        utf8.encode(COMMIT_DOMAIN), ZERO,
        utf8.encode(matchId), ZERO,
        uint32be(handIndex), ZERO,
        hexToBytes(serverSeed),
    ]));
}
/**
 * One per seat, published in the same breath as `commitment`, over the seed the
 * server will use for that seat if its client contributes nothing.
 *
 * This is the commitment the first version of the scheme did not have, and its
 * absence was the whole hole: fill seeds used to be drawn at *deal* time, with
 * every client contribution already in hand, so the server could re-roll the
 * seats nobody was watching until the deal suited it — while `verifyDeal` still
 * passed, because it only ever checked `serverSeed`.
 *
 * The seat index is inside the preimage for a reason: without it the four
 * committed fill seeds would be interchangeable, and a server that had seen the
 * contributions could assign them to whichever seats produced the deal it
 * wanted — 4! = 24 free deals per hand, every commitment still opening.
 */
export function seatCommitment(matchId, handIndex, seat, fillSeed) {
    assertSeed(fillSeed, `fillSeeds[${seat}]`);
    return sha256Hex(concat([
        utf8.encode(SEAT_COMMIT_DOMAIN), ZERO,
        utf8.encode(matchId), ZERO,
        uint32be(handIndex), ZERO,
        uint32be(seat), ZERO,
        hexToBytes(fillSeed),
    ]));
}
/** `seatCommitment` for every seat, in seat order — the array published in
 *  `FairnessPublic.seatCommitments`. */
export function seatCommitments(matchId, handIndex, fillSeeds) {
    return fillSeeds.map((seed, seat) => seatCommitment(matchId, handIndex, seat, seed));
}
/**
 * Mixes the server's committed seed with every seat's seed. No separators are
 * needed after the domain tag because each seed is exactly SEED_BYTES, so the
 * preimage is unambiguous by length alone.
 */
export function dealSeed(serverSeed, seatSeeds) {
    assertSeed(serverSeed, 'serverSeed');
    seatSeeds.forEach((s, i) => assertSeed(s, `seatSeeds[${i}]`));
    return sha256Hex(concat([utf8.encode(DEAL_SEED_DOMAIN), hexToBytes(serverSeed), ...seatSeeds.map(hexToBytes)]));
}
/**
 * Which seed each seat ends up contributing, and how that is recorded publicly.
 *
 * The reducer and the verifier both call this rather than each deciding for
 * itself. Two implementations of *this* choice is precisely how a deal becomes
 * unverifiable: the server would deal from one reading of the record and the
 * player would check against another, and the mismatch would look like fraud
 * (or hide it). Throws rather than returning a partial answer — a seat with no
 * usable seed is not a deal that should happen.
 */
export function resolveSeatSeeds(contributions, fillSeeds) {
    if (contributions.length !== fillSeeds.length) {
        throw new Error(`fairness record is inconsistent: ${contributions.length} contributions for ${fillSeeds.length} committed fill seeds`);
    }
    const seatSeeds = [];
    const published = [];
    contributions.forEach((contributed, seat) => {
        if (isSeedHex(contributed)) {
            seatSeeds.push(contributed);
            published.push(contributed);
            return;
        }
        const fill = fillSeeds[seat];
        // Unreachable on a server that committed properly; reachable on one that
        // did not, which is the case worth failing loudly for.
        if (!isSeedHex(fill))
            throw new Error(`seat ${seat} has no committed fill seed`);
        seatSeeds.push(fill);
        published.push(CONTRIBUTION_SERVER);
    });
    return { seatSeeds, contributions: published };
}
function assertSeed(seed, what) {
    if (!isSeedHex(seed))
        throw new Error(`${what} must be ${SEED_BYTES * 2} lowercase hex characters`);
}
/** block_i = SHA-256(dealSeed ‖ uint32be(i)); bytes are consumed in order. */
function byteStream(seed) {
    let block = new Uint8Array(0);
    let at = 0;
    let counter = 0;
    const nextByte = () => {
        if (at >= block.length) {
            block = sha256(concat([seed, uint32be(counter++)]));
            at = 0;
        }
        return block[at++];
    };
    const nextUint32 = () => ((nextByte() << 24) | (nextByte() << 16) | (nextByte() << 8) | nextByte()) >>> 0;
    return {
        /**
         * Rejection sampling, not `u % n`: 2^32 is not a multiple of any deck size
         * we deal, so a plain modulo would make the low residues one draw more
         * likely than the high ones. The bias is tiny (~1 in 5×10^7 for n = 52) but
         * it is free to remove, and a fairness page that claims "unbiased" should
         * not have to qualify the claim.
         */
        below(n) {
            const limit = 0x100000000 - (0x100000000 % n);
            for (;;) {
                const u = nextUint32();
                if (u < limit)
                    return u % n;
            }
        },
    };
}
/** Fisher–Yates over the module's canonical deck order. */
export function shuffle(deck, seed) {
    assertSeed(seed, 'dealSeed');
    const stream = byteStream(hexToBytes(seed));
    const out = [...deck];
    for (let i = out.length - 1; i >= 1; i--) {
        const j = stream.below(i + 1);
        const tmp = out[i];
        out[i] = out[j];
        out[j] = tmp;
    }
    return out;
}
/** The whole pipeline: revealed seeds → permutation → holdings/kitty/stock. */
export function dealFromSeeds(deck, plan, table, dealer, serverSeed, seatSeeds) {
    const seed = dealSeed(serverSeed, seatSeeds);
    return { dealSeed: seed, deal: dealFromPermutation(shuffle(deck, seed), plan, table, dealer) };
}
const sameCards = (a, b) => a.length === b.length && a.every((c, i) => c === b[i]);
const emptyDeal = () => ({ deckOrder: [], holdings: [], kitty: [], stock: [] });
/**
 * What a client runs against a revealed hand — and what a sceptic runs from a
 * shell, given nothing but an exported receipt (`verifyReceipt`).
 *
 * Every input is public except `commitment`/`seatCommitments`, which the client
 * must have stored itself at `committing` time, and `mySeed`, which it stored
 * before sending. Comparing against a commitment the server hands over
 * afterwards verifies nothing at all — it is the server's own two halves shown
 * at the same moment — which is why those are separate parameters rather than
 * being read out of the reveal.
 */
export function verifyDeal(params) {
    const failures = [];
    let commitmentMatches = false;
    try {
        commitmentMatches =
            commitment(params.matchId, params.handIndex, params.serverSeed) === params.commitment;
    }
    catch (e) {
        failures.push(`server seed is malformed: ${e.message}`);
    }
    if (!commitmentMatches)
        failures.push('revealed server seed does not match the published commitment');
    // --- the per-seat half: this is what stops the server choosing a seed after
    // it has seen a client's. Every seat seed must be accounted for by something
    // fixed before the contributions were visible, or by a contribution itself.
    const seatCount = params.seatSeeds.length;
    let seatCommitmentsMatch = true;
    if (params.seatCommitments.length !== seatCount || params.fillSeeds.length !== seatCount) {
        seatCommitmentsMatch = false;
        failures.push(`the reveal covers ${seatCount} seats but carries ${params.seatCommitments.length} commitments and ${params.fillSeeds.length} fill seeds`);
    }
    if (params.contributions.length !== seatCount) {
        failures.push(`the contribution record covers ${params.contributions.length} seats, not ${seatCount}`);
    }
    const seats = [];
    const serverFilledSeats = [];
    for (let seat = 0; seat < seatCount; seat++) {
        const fill = params.fillSeeds[seat];
        const published = params.seatCommitments[seat];
        let opens = false;
        try {
            opens = published !== undefined && seatCommitment(params.matchId, params.handIndex, seat, fill) === published;
        }
        catch {
            opens = false;
        }
        if (!opens) {
            seatCommitmentsMatch = false;
            failures.push(`seat ${seat}: the revealed fill seed does not open the commitment published for that seat`);
        }
        const contributed = params.contributions[seat];
        const used = params.seatSeeds[seat];
        let source;
        if (isSeedHex(contributed) && used === contributed) {
            // 'mine' requires the bytes to match the ones this device stored, not
            // merely the seat to be ours: a server that invents a contribution for
            // your seat must not have it rendered back to you as your own entropy.
            source = params.mySeed !== undefined && used === params.mySeed ? 'mine' : 'otherClient';
        }
        else if (!isSeedHex(contributed) && used === fill) {
            source = 'committed';
            serverFilledSeats.push(seat);
        }
        else {
            source = 'unaccounted';
            failures.push(`seat ${seat}: the seed the deal used is neither the contribution on record nor the committed fill seed`);
        }
        seats.push({ seat, source, commitmentOpens: opens });
    }
    let mySeedIncluded = null;
    let mySeedStatus = 'unknown';
    if (params.mySeat !== undefined) {
        const seat = params.mySeat;
        const used = params.seatSeeds[seat];
        const onRecord = params.contributions[seat];
        if (params.mySeed !== undefined) {
            if (used === params.mySeed) {
                mySeedIncluded = true;
                mySeedStatus = 'used';
            }
            else if (!isSeedHex(onRecord) && used === params.fillSeeds[seat]) {
                // Sent too late, or dropped on the way. Not a failure: the seat used the
                // seed committed before the deal, so nothing was chosen late — but this
                // player's bytes are not in the hand and the view must say so rather
                // than show them a tick they did not earn.
                mySeedIncluded = false;
                mySeedStatus = 'lateOrDropped';
            }
            else {
                mySeedIncluded = false;
                mySeedStatus = 'contradicted';
                failures.push('the seed recorded at this seat is neither the entropy this client sent nor the seed committed for the seat');
            }
        }
        else if (params.iContributed === false && isSeedHex(onRecord)) {
            // A contribution recorded for a seat whose client sent nothing is a seed
            // the server chose after seeing everyone else's — the exact freedom the
            // commitments exist to remove, wearing a client's name.
            mySeedIncluded = false;
            mySeedStatus = 'contradicted';
            failures.push('the record claims this seat contributed entropy, but this device sent none');
        }
    }
    let seed;
    let deal;
    try {
        const derived = dealFromSeeds(params.deck, params.plan, params.table, params.dealer, params.serverSeed, params.seatSeeds);
        seed = derived.dealSeed;
        deal = derived.deal;
    }
    catch (e) {
        return {
            ok: false,
            commitmentMatches,
            seatCommitmentsMatch,
            seats,
            serverFilledSeats,
            mySeedIncluded,
            mySeedStatus,
            holdingsMatch: null,
            kittyMatch: null,
            deckOrderMatches: null,
            dealSeed: '',
            deckOrder: [],
            deal: emptyDeal(),
            failures: [...failures, `could not re-derive the deal: ${e.message}`],
        };
    }
    const expected = params.expected;
    let holdingsMatch = null;
    let kittyMatch = null;
    let deckOrderMatches = null;
    if (expected?.dealSeed !== undefined && expected.dealSeed !== seed) {
        failures.push('re-derived deal seed differs from the recorded one');
    }
    if (expected?.deckOrder) {
        deckOrderMatches = sameCards(expected.deckOrder, deal.deckOrder);
        if (!deckOrderMatches)
            failures.push('re-derived permutation differs from the recorded one');
    }
    if (expected?.holdings) {
        holdingsMatch =
            expected.holdings.length === deal.holdings.length &&
                expected.holdings.every((h, s) => sameCards(h, deal.holdings[s]));
        if (!holdingsMatch)
            failures.push('re-derived holdings differ from the recorded deal');
    }
    if (expected?.kitty) {
        kittyMatch = sameCards(expected.kitty, deal.kitty);
        if (!kittyMatch)
            failures.push('re-derived kitty differs from the recorded deal');
    }
    return {
        ok: failures.length === 0,
        commitmentMatches,
        seatCommitmentsMatch,
        seats,
        serverFilledSeats,
        mySeedIncluded,
        mySeedStatus,
        holdingsMatch,
        kittyMatch,
        deckOrderMatches,
        dealSeed: seed,
        deckOrder: deal.deckOrder,
        deal,
        failures,
    };
}
// ---------------------------------------------------------------- receipts
export const RECEIPT_FORMAT = 'cards.fairness.receipt.v2';
export function verifyReceipt(receipt) {
    if (receipt.format !== RECEIPT_FORMAT) {
        // Refusing an unknown format beats checking it under the wrong rules: a v1
        // receipt has no seat commitments, so "verified" against v2 rules would be
        // a claim about bytes that were never published.
        return {
            ok: false,
            commitmentMatches: false,
            seatCommitmentsMatch: false,
            seats: [],
            serverFilledSeats: [],
            mySeedIncluded: null,
            mySeedStatus: 'unknown',
            holdingsMatch: null,
            kittyMatch: null,
            deckOrderMatches: null,
            dealSeed: '',
            deckOrder: [],
            deal: emptyDeal(),
            failures: [`unknown receipt format ${JSON.stringify(receipt.format)}; this checker reads ${RECEIPT_FORMAT}`],
        };
    }
    return verifyDeal({
        matchId: receipt.matchId,
        handIndex: receipt.handIndex,
        deck: receipt.deck,
        plan: receipt.plan,
        table: receipt.table,
        dealer: receipt.dealer,
        commitment: receipt.commitment,
        seatCommitments: receipt.seatCommitments,
        contributions: receipt.contributions,
        serverSeed: receipt.reveal.serverSeed,
        seatSeeds: receipt.reveal.seatSeeds,
        fillSeeds: receipt.reveal.fillSeeds,
        expected: receipt.dealt,
        mySeat: receipt.mySeat,
        mySeed: receipt.mySeed,
    });
}
//# sourceMappingURL=fairness.js.map